// ─── Flujo piloto: la cadena de un pedido, renglón por renglón ──────────────
// CT → PV → RQ → OC → RP → RS (la RS genera la FV y la RP la FP), como en el
// panel de ventas. El pedido (PV) es la misma OP de siempre; la OC es la OCP,
// la RP es cada recepción registrada en la OCP, la FP es su causación, la RS es
// la remisión y la FV la factura. Este módulo NO crea nada: LEE los documentos
// que ya existen y dice, por cada renglón del pedido, en qué va.
//
// Reglas que vienen del panel y que no se deben romper:
//   · El estado NUNCA se escribe: sale de sumar cantidades por renglón
//     (pedido, en bodega, comprado, recibido, despachado, facturado).
//   · Una remisión en borrador no despachó nada (todavía no movió el kardex) y
//     una cancelada tampoco. Una factura anulada no facturó nada.
//   · Lo que no se puede saber no se inventa: un renglón que no aparece en
//     ningún documento posterior queda en cero, y así se muestra.
//   · Lo que falta por entregar se reparte en cuatro partes que SUMAN EXACTO:
//     en bodega · comprado (en camino) · pedido a compras (RQ) · sin pedir.
//     «Sin pedir» es la alarma: nadie ha movido un dedo por esa mercancía.
//
// Es puro: todo llega en `ctx` (los documentos, y funciones para el stock, los
// componentes de un kit y el costo), así se prueba sin navegador.

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
  .toLowerCase().replace(/\s+/g, " ").trim();
const num = (v) => { const n = Number(String(v ?? "").replace(/,/g, ".")); return Number.isFinite(n) ? n : 0; };
const EPS = 1e-6;

const remisionDespacho = (rc) => !!rc && rc.estado !== "cancelada" && rc.estado !== "borrador";
const origenesDe = (d) => [d && d._origen, ...((d && Array.isArray(d._origenes)) ? d._origenes : [])].filter(Boolean);
const esInformativa = (o) => !!(o && o._informativa);

// ¿Esta factura cubre cantidades de esta OP? (directa, o por una de sus remisiones)
function facturaDeOP(f, op, remisiones) {
  if (!f || f.estado === "anulada") return false;
  return origenesDe(f).some(o => {
    if (o.tipo === "OP") return o.id === op.id;
    if (o.tipo === "RC") {
      const rc = remisiones.find(r => r.id === o.id);
      return !!(rc && rc._origen && rc._origen.tipo === "OP" && rc._origen.id === op.id);
    }
    return false;
  });
}

const suma = (docs, articulo) => docs.reduce((s, d) => s + (d.items || [])
  .filter(it => norm(it.articulo) === norm(articulo))
  .reduce((t, it) => t + num(it.cantidad), 0), 0);

function recibidoEn(ocp, articulo) {
  return ((ocp && ocp._recepciones) || []).reduce((s, r) => s + (r.items || [])
    .filter(it => norm(it.articulo) === norm(articulo))
    .reduce((t, it) => t + num(it.cantidad), 0), 0);
}

// La fecha prometida: la primera entrega programada de la OP.
function fechaEntrega(op) {
  const f = ((op && op._entregas) || []).map(e => e && e.fecha).filter(Boolean).sort();
  return f[0] || null;
}

// Estado de un documento de compra, en palabras.
export function estadoOCP(o) {
  if (!o) return null;
  if (o.estado === "cancelada") return "cancelada";
  if (esInformativa(o)) return o._cerrada ? "cerrada" : "abierta";
  const pasos = [];
  const todo = (o.items || []).every(it => recibidoEn(o, it.articulo) + EPS >= num(it.cantidad));
  const algo = (o._recepciones || []).length > 0;
  pasos.push(todo && algo ? "recibida" : algo ? "recibida en parte" : "por recibir");
  if ((o._facturasContables || []).length) pasos.push("causada");
  if (o.estado_pago === "pagada") pasos.push("pagada");
  return pasos.join(" · ");
}

// ─── Los documentos que cuelgan de una OP ───────────────────────────────────
export function documentosDeOP(op, ctx) {
  const ocps = (ctx.ocp || []).filter(o => o._origen && o._origen.tipo === "OP" && o._origen.id === op.id && o.estado !== "cancelada");
  const remisiones = (ctx.remisiones || []).filter(rc => rc._origen && rc._origen.tipo === "OP" && rc._origen.id === op.id && rc.estado !== "cancelada");
  const facturas = (ctx.facturas || []).filter(f => facturaDeOP(f, op, ctx.remisiones || []));
  const cxps = (ctx.cuentas_pagar || []).filter(x => x._origen && x._origen.id === op.id);
  const ct = op._origen && op._origen.tipo === "CT"
    ? ((ctx.cotizaciones || []).find(c => c.id === op._origen.id) || { id: op._origen.id, numero: op._origen.numero, _noEsta: true })
    : null;
  const rqs = (ctx.requisiciones || []).filter(r => r.pedido_id === op.id && r.estado !== "anulada");
  return {
    ct, rqs,
    oficiales: ocps.filter(o => !esInformativa(o)),
    informativas: ocps.filter(esInformativa),
    remisiones,
    despachos: remisiones.filter(remisionDespacho),
    facturas,
    cxps,
    // La FP es la causación de la OCP: cada factura de proveedor registrada.
    causaciones: ocps.filter(o => !esInformativa(o)).flatMap(o => (o._facturasContables || []).map(fc => ({ ocp: o, fc })))
  };
}

// ─── La cadena de un pedido, sin repartir la bodega ─────────────────────────
// Por cada renglón: lo pedido, lo despachado, lo facturado y, por cada artículo
// que hay que COMPRAR para entregarlo (el mismo artículo, o las partes si es un
// kit), lo comprado y lo recibido en las OCP de esta orden.
export function cadenaDeOP(op, ctx) {
  const docs = documentosDeOP(op, ctx);
  const componentes = ctx.componentes || (() => []);
  const lineas = (op.items || []).map((it, idx) => {
    const articulo = it.articulo || "";
    const pedida = num(it.cantidad);
    const despachada = suma(docs.despachos, articulo);
    const facturada = suma(docs.facturas, articulo);
    const comps = componentes(op, articulo) || [];
    const esKit = comps.length > 0;
    const debe = Math.max(0, pedida - despachada);
    const aComprar = (esKit ? comps : [{ articulo, porUnidad: 1 }]).map(c => {
      const ocps = docs.oficiales
        .map(o => ({ o, cantidad: suma([o], c.articulo), recibido: recibidoEn(o, c.articulo) }))
        .filter(x => x.cantidad > 0 || x.recibido > 0);
      const comprado = ocps.reduce((s, x) => s + x.cantidad, 0);
      const recibido = ocps.reduce((s, x) => s + x.recibido, 0);
      return {
        articulo: c.articulo, porUnidad: c.porUnidad, esParte: esKit,
        necesita: Math.ceil(c.porUnidad * debe - EPS),
        despachado: c.porUnidad * despachada,
        comprado, recibido, enCamino: Math.max(0, comprado - recibido),
        ocps: ocps.map(x => ({ id: x.o.id, numero: x.o.numero, cantidad: x.cantidad, recibido: x.recibido, estado: estadoOCP(x.o) }))
      };
    });
    return {
      idx, articulo, descripcion: it.descripcion || "", pedida, precio: num(it.precio_unitario),
      despachada, facturada, debe, esKit, aComprar,
      remisiones: docs.despachos.filter(rc => suma([rc], articulo) > 0).map(rc => ({ id: rc.id, numero: rc.numero, cantidad: suma([rc], articulo), estado: rc.estado })),
      facturasL: docs.facturas.filter(f => suma([f], articulo) > 0).map(f => ({ id: f.id, numero: f.numero, cantidad: suma([f], articulo) }))
    };
  });

  // Servicios: los costos asociados que NO son partes de un kit (marcación,
  // transporte, un producto comprado aparte). Van por orden de compra, orden
  // informativa o cuenta de cobro.
  const partes = new Set(lineas.flatMap(l => l.esKit ? l.aComprar.map(c => norm(c.articulo) + "|" + norm(l.articulo)) : []));
  const servicios = (op._conceptosCosto || [])
    .filter(c => !c._esComision)
    .filter(c => !(c.articulo && c._articuloOrigen && partes.has(norm(c.articulo) + "|" + norm(c._articuloOrigen))))
    .map(c => servicioDe(c, ctx));

  return { op, docs, lineas, servicios, entrega: fechaEntrega(op) };
}

function servicioDe(c, ctx) {
  const ocp = c._ocpId ? (ctx.ocp || []).find(o => o.id === c._ocpId) || null : null;
  const cxp = c._cxpId ? (ctx.cuentas_pagar || []).find(x => x.id === c._cxpId) || null : null;
  const cc = cxp && cxp._cuentaCobroId ? (ctx.cuentas_cobro || []).find(k => k.id === cxp._cuentaCobroId) || null : null;
  const cantidad = num(c.cantidad), valor = num(c.valor_tercero);
  let estado, pendiente;
  if (ocp) { estado = `${ocp.numero || "OCP"} · ${estadoOCP(ocp)}`; pendiente = esInformativa(ocp) ? !ocp._cerrada : ocp.estado_pago !== "pagada"; }
  else if (cxp) {
    estado = `${cxp.numero || "CxP"} · ${cxp.estado === "pagada" ? "pagada" : cc ? `en ${cc.numero || "cuenta de cobro"}` : "pendiente de cuenta de cobro"}`;
    pendiente = cxp.estado !== "pagada";
  }
  else if (c._via === "nada") { estado = "no genera documento"; pendiente = false; }
  else if (!c._via) { estado = "sin destino: no se ha decidido si va a orden de compra o cuenta de cobro"; pendiente = true; }
  else if (!(c.proveedor_nombre || "").trim()) { estado = "falta el tercero"; pendiente = true; }
  else { estado = "sin documento todavía"; pendiente = true; }
  return {
    ccid: c._ccid || null,
    concepto: c.concepto || "", articulo: c.articulo || "", parte: c._articuloOrigen || "",
    tercero: c.proveedor_nombre || "", cantidad, valor, total: cantidad * valor,
    via: c._via || "", ocp, cxp, cc, estado, pendiente,
    sinDocumento: !ocp && !cxp && c._via !== "nada"
  };
}

// ─── Material en transición: repartir lo que falta en cuatro partes ─────────
// Lo pendiente de cada renglón (debe = pedido − despachado) se cubre, en este
// orden: con lo que hay en BODEGA, con lo COMPRADO que viene en camino, con lo
// PEDIDO A COMPRAS (requisiciones, fase 2) y lo que queda está SIN PEDIR.
//
// La bodega es una sola para todos los pedidos, así que se reparte: primero a
// cada orden lo que llegó por SUS propias compras y no ha salido (como el
// reparto de la recepción del panel), y lo que sobra, por antigüedad. Así las
// cuatro partes suman exacto también en el consolidado de todas las órdenes.
export function materialEnTransicion(ops, ctx) {
  const vigentes = (ops || []).filter(op => op && op.estado !== "borrador" && op.estado !== "cancelada")
    .slice().sort((a, b) => (a.fecha || "").localeCompare(b.fecha || "") || String(a.numero || "").localeCompare(String(b.numero || "")));
  const cadenas = vigentes.map(op => cadenaDeOP(op, ctx));
  const stock = ctx.stock || (() => 0);
  const bodega = new Map();
  const disponible = (art) => {
    const k = norm(art);
    if (!bodega.has(k)) bodega.set(k, Math.max(0, num(stock(art))));
    return bodega.get(k);
  };
  const tomar = (art, cant) => { const k = norm(art); const d = disponible(art); const t = Math.max(0, Math.min(d, cant)); bodega.set(k, d - t); return t; };

  const filas = [];
  cadenas.forEach(cad => cad.lineas.forEach(l => l.aComprar.forEach(c => {
    if (c.necesita <= 0) return;
    filas.push({ cad, linea: l, c, apartado: 0, enBodega: 0, comprado: 0, enRQ: 0, sinPedir: 0, apartadoSinStock: 0 });
  })));
  // 0. Lo APARTADO para cada orden (fase 3), con tope en lo que le falta por
  //    entregar. Va primero: es mercancía que ya tiene dueño. Si la bodega no
  //    alcanza para cubrirlo (alguien la sacó por el flujo de siempre), la
  //    diferencia se anota: el apartado dice que hay y no hay.
  const apartadoTotal = ctx.apartadoTotal || (() => 0);
  const restoApartado = new Map();
  filas.forEach(f => {
    const k = f.cad.op.id + "|" + norm(f.c.articulo);
    if (!restoApartado.has(k)) restoApartado.set(k, Math.max(0, num(apartadoTotal(f.cad.op, f.c.articulo))));
    const a = Math.min(f.c.necesita, restoApartado.get(k));
    restoApartado.set(k, restoApartado.get(k) - a);
    f.apartado = a;
    f.enBodega = tomar(f.c.articulo, a);
    f.apartadoSinStock = a - f.enBodega;
  });
  // 1. Lo que llegó por las compras de la propia orden y sigue en bodega
  //    (recepciones de antes del apartado).
  filas.forEach(f => {
    const propio = Math.max(0, f.c.recibido - f.c.despachado - f.enBodega);
    f.enBodega += tomar(f.c.articulo, Math.min(f.c.necesita - f.enBodega, propio));
  });
  // 2. El resto de la bodega, a la orden más antigua primero.
  filas.forEach(f => { f.enBodega += tomar(f.c.articulo, f.c.necesita - f.enBodega); });
  // 3 y 4. Lo comprado en camino, lo pedido a compras y lo que nadie ha pedido.
  const rq = ctx.rqPendiente || (() => 0);
  filas.forEach(f => {
    let resto = f.c.necesita - f.enBodega;
    f.comprado = Math.min(resto, f.c.enCamino); resto -= f.comprado;
    f.enRQ = Math.min(resto, Math.max(0, num(rq(f.cad.op, f.c.articulo)))); resto -= f.enRQ;
    f.sinPedir = Math.max(0, resto);
    f.costo = num(ctx.costo ? ctx.costo(f.c.articulo) : 0);
  });
  return { cadenas, filas };
}

// ─── Estado del pedido y qué sigue ──────────────────────────────────────────
// Como `pvMioResumen` del panel: por remisionar → parcial → por facturar →
// facturado. Se mide en unidades y lo de más en un renglón no tapa lo que falta
// en otro. (Cobro y pago a terceros los sigue Control de Órdenes.)
export const ESTADOS_PEDIDO = {
  por_remisionar: { label: "Por remisionar", color: "#b45309", bg: "#fef3c7" },
  parcial:        { label: "Remisionado en parte", color: "#c2410c", bg: "#ffedd5" },
  por_facturar:   { label: "Por facturar", color: "#1d4ed8", bg: "#dbeafe" },
  facturado:      { label: "Facturado", color: "#047857", bg: "#d1fae5" },
  borrador:       { label: "Borrador", color: "#64748b", bg: "#f1f5f9" },
  cancelada:      { label: "Cancelado", color: "#991b1b", bg: "#fee2e2" }
};
export const POR_GESTIONAR = ["por_remisionar", "parcial", "por_facturar"];

export function resumenPedido(cad, filasTransicion, hoy, avisos = []) {
  const { op, lineas } = cad;
  const pedida = lineas.reduce((s, l) => s + l.pedida, 0);
  const despachada = lineas.reduce((s, l) => s + Math.min(l.despachada, l.pedida), 0);
  const facturada = lineas.reduce((s, l) => s + Math.min(l.facturada, l.pedida), 0);
  const pendiente = Math.max(0, pedida - despachada);
  let estado;
  if (op.estado === "borrador") estado = "borrador";
  else if (op.estado === "cancelada") estado = "cancelada";
  else if (pendiente > EPS) estado = despachada > EPS ? "parcial" : "por_remisionar";
  else if (facturada + EPS < pedida) estado = "por_facturar";
  else estado = "facturado";

  const mias = (filasTransicion || []).filter(f => f.cad.op.id === op.id);
  const t = mias.reduce((a, f) => ({ enBodega: a.enBodega + f.enBodega, comprado: a.comprado + f.comprado, enRQ: a.enRQ + f.enRQ, sinPedir: a.sinPedir + f.sinPedir }),
    { enBodega: 0, comprado: 0, enRQ: 0, sinPedir: 0 });
  const listosParaRemisionar = lineas.filter(l => l.debe > 0 && mias.filter(f => f.linea === l).every(f => f.enBodega >= f.c.necesita)).length;
  const serviciosSinDoc = cad.servicios.filter(s => s.sinDocumento && s.pendiente).length;
  const atrasado = !!(cad.entrega && hoy && cad.entrega < hoy && pendiente > EPS && POR_GESTIONAR.includes(estado));
  const porFacturarValor = lineas.reduce((s, l) => s + Math.max(0, Math.min(l.despachada, l.pedida) - l.facturada) * l.precio, 0);

  const que = [];
  if (atrasado) que.push(`⏰ Entrega vencida el ${cad.entrega}`);
  if (avisos.length && POR_GESTIONAR.includes(estado)) que.push(`📦 Llegó material (${avisos.map(r => r.numero).join(", ")}): listo para remisionar`);
  if (estado === "por_remisionar" || estado === "parcial") {
    if (listosParaRemisionar) que.push(`📦 ${listosParaRemisionar} ${listosParaRemisionar === 1 ? "renglón" : "renglones"} con todo en bodega: se puede remisionar`);
    else if (t.enBodega > 0) que.push(`📦 Hay mercancía en bodega para remisionar en parte`);
    if (t.sinPedir > 0) que.push(`🚨 ${t.sinPedir} ud${t.sinPedir === 1 ? "" : "s"} sin pedir a compras: haz la requisición`);
    if (t.comprado > 0) que.push(`🚚 Esperando ${t.comprado} ud${t.comprado === 1 ? "" : "s"} compradas`);
    if (t.enRQ > 0) que.push(`📝 ${t.enRQ} ud${t.enRQ === 1 ? "" : "s"} pedidas a compras, por ordenar`);
  }
  if (estado === "por_facturar" || (estado === "parcial" && porFacturarValor > 0.5)) que.push(`🧾 Entregado sin facturar: $ ${Math.round(porFacturarValor).toLocaleString("es-CO")}`);
  if (serviciosSinDoc && POR_GESTIONAR.includes(estado)) que.push(`🛠️ ${serviciosSinDoc} servicio${serviciosSinDoc === 1 ? "" : "s"} sin orden ni cuenta por pagar`);
  if (!que.length) que.push(estado === "facturado" ? "✅ Entregado y facturado" : "—");

  return { estado, pedida, despachada, facturada, pendiente, transicion: t, listosParaRemisionar, serviciosSinDoc, atrasado, porFacturarValor, avisos, queHacer: que };
}
